import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  BookOpen,
  Bot,
  Brain,
  Braces,
  CalendarClock,
  CirclePlay,
  Cloud,
  Compass,
  Cpu,
  Crown,
  Database,
  Feather,
  FileText,
  FilePlus2,
  Flag,
  Flame,
  Gem,
  GitBranch,
  GitMerge,
  Globe,
  Hourglass,
  LayoutGrid,
  Lightbulb,
  ListTree,
  Mail,
  MessageSquare,
  MessageSquareMore,
  Network,
  Package,
  Plus,
  PlusCircle,
  RefreshCcw,
  Rocket,
  ScanEye,
  Search,
  ShieldAlert,
  Shuffle,
  Sparkles,
  Split,
  Star,
  SunMedium,
  Target,
  Trash2,
  Terminal,
  UserPlus,
  Users,
  Waypoints,
  Webhook,
  Wrench,
  Zap,
} from 'lucide-react';

/** Stable category ids for filters, permissions, analytics, and bulk palette operations. */
export const SIDEBAR_CATEGORIES = {
  TRIGGERS: 'triggers',
  LLMS: 'llms',
  DATA_MODELS_MCPS: 'data_models_mcps',
  /** Knowledge-base MCP palette (wire id: `knowledge_base`). */
  KNOWLEDGE_BASE: 'KnowledgeBase',
  AGENTS: 'agents',
  ACTIONS: 'actions',
  FLOW_CONTROL: 'flow_control',
  UTILITIES: 'utilities',
  TERMINAL: 'terminal',
} as const;

export type SidebarCategoryId = (typeof SIDEBAR_CATEGORIES)[keyof typeof SIDEBAR_CATEGORIES];

/**
 * Sidebar section order (excluding TERMINAL). API `sort_order` is only used as a tie-breaker
 * and for categories not listed here.
 */
export const SIDEBAR_PALETTE_SECTION_ORDER: readonly SidebarCategoryId[] = [
  SIDEBAR_CATEGORIES.TRIGGERS,
  SIDEBAR_CATEGORIES.LLMS,
  SIDEBAR_CATEGORIES.DATA_MODELS_MCPS,
  SIDEBAR_CATEGORIES.KNOWLEDGE_BASE,
  SIDEBAR_CATEGORIES.AGENTS,
  SIDEBAR_CATEGORIES.ACTIONS,
  SIDEBAR_CATEGORIES.FLOW_CONTROL,
  SIDEBAR_CATEGORIES.UTILITIES,
] as const;

export const SIDEBAR_COLLAPSIBLE_CATEGORY_ORDER: readonly SidebarCategoryId[] = SIDEBAR_PALETTE_SECTION_ORDER;

const SIDEBAR_SECTION_ORDER_RANK = new Map<SidebarCategoryId, number>(
  SIDEBAR_PALETTE_SECTION_ORDER.map((id, i) => [id, i])
);

const KNOWN_SIDEBAR_CATEGORY = new Set<string>(Object.values(SIDEBAR_CATEGORIES));

/** Sort WorkflowBuilderControls categories for palette display (fixed primary order). */
export function compareWorkflowBuilderCategoryRows(
  a: { category: string; sort_order: number },
  b: { category: string; sort_order: number }
): number {
  const rank = (cat: string): number => {
    if (cat === SIDEBAR_CATEGORIES.TERMINAL) return 9999;
    if (!KNOWN_SIDEBAR_CATEGORY.has(cat)) return 9000;
    return SIDEBAR_SECTION_ORDER_RANK.get(cat as SidebarCategoryId) ?? 8000;
  };
  const ra = rank(a.category);
  const rb = rank(b.category);
  if (ra !== rb) return ra - rb;
  return a.sort_order - b.sort_order;
}

export const SIDEBAR_CATEGORY_LABELS: Record<SidebarCategoryId, string> = {
  [SIDEBAR_CATEGORIES.TRIGGERS]: 'TRIGGERS',
  [SIDEBAR_CATEGORIES.LLMS]: 'LLMs',
  [SIDEBAR_CATEGORIES.DATA_MODELS_MCPS]: 'DATA MODELS (MCPs)',
  [SIDEBAR_CATEGORIES.KNOWLEDGE_BASE]: 'KnowledgeBase (MCP)',
  [SIDEBAR_CATEGORIES.AGENTS]: 'AGENTS',
  [SIDEBAR_CATEGORIES.ACTIONS]: 'ACTIONS',
  [SIDEBAR_CATEGORIES.FLOW_CONTROL]: 'FLOW CONTROL',
  [SIDEBAR_CATEGORIES.UTILITIES]: 'UTILITIES',
  [SIDEBAR_CATEGORIES.TERMINAL]: 'END',
};

/** Category header icon (visual grouping only). */
export const SIDEBAR_CATEGORY_ICONS: Record<SidebarCategoryId, LucideIcon> = {
  [SIDEBAR_CATEGORIES.TRIGGERS]: Zap,
  [SIDEBAR_CATEGORIES.LLMS]: Cpu,
  [SIDEBAR_CATEGORIES.DATA_MODELS_MCPS]: Database,
  [SIDEBAR_CATEGORIES.KNOWLEDGE_BASE]: BookOpen,
  [SIDEBAR_CATEGORIES.AGENTS]: Bot,
  [SIDEBAR_CATEGORIES.ACTIONS]: Package,
  [SIDEBAR_CATEGORIES.FLOW_CONTROL]: GitBranch,
  [SIDEBAR_CATEGORIES.UTILITIES]: Wrench,
  [SIDEBAR_CATEGORIES.TERMINAL]: Flag,
};

/** Lucide icons rotated per agent control (stable index from control id). */
const AGENT_PALETTE_LUCIDE_ICONS: LucideIcon[] = [
  Brain,
  Sparkles,
  Gem,
  Rocket,
  Lightbulb,
  Star,
  Target,
  Compass,
  Crown,
  Feather,
  Flame,
  Network,
  ScanEye,
  Bot,
  MessageSquare,
  Users,
  UserPlus,
  Zap,
];

const AGENT_CANVAS_EMOJIS = ['🤖', '✨', '🎯', '🔮', '🧠', '🦾', '💡', '🌟', '🚀', '⚡', '🎭', '🔭', '💎', '🛰️', '🧩', '🎪'];

function hashString32(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}

/** Stable icon + canvas emoji for agent palette rows (API icon ref ignored for agents). */
export function pickAgentPalettePresentation(stableControlKey: string): {
  Icon: LucideIcon;
  canvasIcon: string;
} {
  const h = hashString32(stableControlKey);
  const i = Math.abs(h) % AGENT_PALETTE_LUCIDE_ICONS.length;
  const e = Math.abs(h >>> 0) % AGENT_CANVAS_EMOJIS.length;
  return {
    Icon: AGENT_PALETTE_LUCIDE_ICONS[i],
    canvasIcon: AGENT_CANVAS_EMOJIS[e],
  };
}

export type PaletteNodeItem = {
  kind: 'node';
  /** API `unique_id` (stable palette row key). */
  id: string;
  category: SidebarCategoryId;
  /** Category row `uuid` from WorkflowBuilderControls when present. */
  categoryUuid?: string;
  /** Serialized onto the canvas / persisted as the node type discriminator. */
  type: string;
  label: string;
  /** Shown on the flow canvas (`CommonNode` uses string icons). */
  canvasIcon: string;
  Icon: LucideIcon;
  /** API `resource_category` (e.g. Manual data models). */
  resourceCategory?: string;
  /** Control row `uuid` from WorkflowBuilderControls. */
  controlUuid?: string;
};

export type PaletteActionItem = {
  kind: 'action';
  /** API `unique_id`. */
  id: string;
  category: SidebarCategoryId;
  categoryUuid?: string;
  actionId: string;
  label: string;
  Icon: LucideIcon;
  /** Control row `uuid` from WorkflowBuilderControls. */
  controlUuid?: string;
};

export type SidebarPaletteItem = PaletteNodeItem | PaletteActionItem;

export const SIDEBAR_PALETTE_ITEMS: SidebarPaletteItem[] = [
  // TRIGGERS (see flowBuilderConsole + TriggerNode spec)
  // {
  //   kind: 'node',
  //   id: 'trigger-manual',
  //   category: SIDEBAR_CATEGORIES.TRIGGERS,
  //   type: 'Trigger_Manual',
  //   label: 'Manual',
  //   canvasIcon: '▶',
  //   Icon: CirclePlay,
  // },
  // {
  //   kind: 'node',
  //   id: 'trigger-webhook',
  //   category: SIDEBAR_CATEGORIES.TRIGGERS,
  //   type: 'Trigger_Webhook',
  //   label: 'Webhook',
  //   canvasIcon: '🔗',
  //   Icon: Webhook,
  // },
  // {
  //   kind: 'node',
  //   id: 'trigger-scheduled',
  //   category: SIDEBAR_CATEGORIES.TRIGGERS,
  //   type: 'Trigger_Scheduled',
  //   label: 'Scheduled',
  //   canvasIcon: '⏰',
  //   Icon: CalendarClock,
  // },
  // {
  //   kind: 'node',
  //   id: 'trigger-event',
  //   category: SIDEBAR_CATEGORIES.TRIGGERS,
  //   type: 'Trigger_Event',
  //   label: 'Event',
  //   canvasIcon: '⚡',
  //   Icon: Zap,
  // },

  // // LLMs
  // {
  //   kind: 'node',
  //   id: 'llm-claude-opus',
  //   category: SIDEBAR_CATEGORIES.LLMS,
  //   type: 'LLM_ClaudeOpus',
  //   label: 'Claude Opus',
  //   canvasIcon: '✨',
  //   Icon: Sparkles,
  // },
  // {
  //   kind: 'node',
  //   id: 'llm-claude-sonnet',
  //   category: SIDEBAR_CATEGORIES.LLMS,
  //   type: 'LLM_ClaudeSonnet',
  //   label: 'Claude Sonnet',
  //   canvasIcon: '🌤️',
  //   Icon: SunMedium,
  // },
  // {
  //   kind: 'node',
  //   id: 'llm-gpt4o',
  //   category: SIDEBAR_CATEGORIES.LLMS,
  //   type: 'LLM_GPT4o',
  //   label: 'GPT-4o',
  //   canvasIcon: '🤖',
  //   Icon: Bot,
  // },
  // {
  //   kind: 'node',
  //   id: 'llm-gemini-pro',
  //   category: SIDEBAR_CATEGORIES.LLMS,
  //   type: 'LLM_GeminiPro',
  //   label: 'Gemini Pro',
  //   canvasIcon: '💎',
  //   Icon: Gem,
  // },
  // {
  //   kind: 'node',
  //   id: 'llm-llama-3',
  //   category: SIDEBAR_CATEGORIES.LLMS,
  //   type: 'LLM_Llama3',
  //   label: 'Llama 3',
  //   canvasIcon: '🦙',
  //   Icon: Cpu,
  // },

  // // DATA MODELS (MCPs)
  // {
  //   kind: 'node',
  //   id: 'mcp-crm-customers',
  //   category: SIDEBAR_CATEGORIES.DATA_MODELS_MCPS,
  //   type: 'MCP_CRM_Customers',
  //   label: 'CRM - Customers',
  //   canvasIcon: '👥',
  //   Icon: Users,
  // },
  // {
  //   kind: 'node',
  //   id: 'mcp-crm-deals',
  //   category: SIDEBAR_CATEGORIES.DATA_MODELS_MCPS,
  //   type: 'MCP_CRM_Deals',
  //   label: 'CRM - Deals',
  //   canvasIcon: '🎯',
  //   Icon: Target,
  // },
  // {
  //   kind: 'node',
  //   id: 'mcp-sales-orders',
  //   category: SIDEBAR_CATEGORIES.DATA_MODELS_MCPS,
  //   type: 'MCP_SalesDB_Orders',
  //   label: 'Sales DB - Orders',
  //   canvasIcon: '🛒',
  //   Icon: Package,
  // },
  // {
  //   kind: 'node',
  //   id: 'mcp-gdrive-docs',
  //   category: SIDEBAR_CATEGORIES.DATA_MODELS_MCPS,
  //   type: 'MCP_GDrive_Documents',
  //   label: 'GDrive - Documents',
  //   canvasIcon: '📁',
  //   Icon: Cloud,
  // },
  // {
  //   kind: 'node',
  //   id: 'mcp-event-emit',
  //   category: SIDEBAR_CATEGORIES.DATA_MODELS_MCPS,
  //   type: 'MCP_EventEmit',
  //   label: 'Event (emit)',
  //   canvasIcon: '⚡',
  //   Icon: Zap,
  // },

  // // AGENTS
  // {
  //   kind: 'node',
  //   id: 'agent-data-analyst',
  //   category: SIDEBAR_CATEGORIES.AGENTS,
  //   type: 'Agent_DataAnalyst',
  //   label: 'Data Analyst',
  //   canvasIcon: '📈',
  //   Icon: BarChart3,
  // },
  // {
  //   kind: 'node',
  //   id: 'agent-email-composer',
  //   category: SIDEBAR_CATEGORIES.AGENTS,
  //   type: 'Agent_EmailComposer',
  //   label: 'Email Composer',
  //   canvasIcon: '✉️',
  //   Icon: Mail,
  // },
  // {
  //   kind: 'node',
  //   id: 'agent-data-researcher',
  //   category: SIDEBAR_CATEGORIES.AGENTS,
  //   type: 'Agent_DataResearcher',
  //   label: 'Data Researcher',
  //   canvasIcon: '🔎',
  //   Icon: Search,
  // },
  // {
  //   kind: 'node',
  //   id: 'agent-report-generator',
  //   category: SIDEBAR_CATEGORIES.AGENTS,
  //   type: 'Agent_ReportGenerator',
  //   label: 'Report Generator',
  //   canvasIcon: '📊',
  //   Icon: FileText,
  // },
  // {
  //   kind: 'node',
  //   id: 'agent-approval-router',
  //   category: SIDEBAR_CATEGORIES.AGENTS,
  //   type: 'Agent_ApprovalRouter',
  //   label: 'Approval Router',
  //   canvasIcon: '🔀',
  //   Icon: Waypoints,
  // }, 

  // // ACTIONS
  // {
  //   kind: 'node',
  //   id: 'action-send-email',
  //   category: SIDEBAR_CATEGORIES.ACTIONS,
  //   type: 'Action_SendEmail',
  //   label: 'Send Email',
  //   canvasIcon: '📧',
  //   Icon: Mail,
  // },
  // {
  //   kind: 'node',
  //   id: 'action-send-slack',
  //   category: SIDEBAR_CATEGORIES.ACTIONS,
  //   type: 'Action_SendSlack',
  //   label: 'Send Slack',
  //   canvasIcon: '💬',
  //   Icon: MessageSquare,
  // },
  // {
  //   kind: 'node',
  //   id: 'action-http-request',
  //   category: SIDEBAR_CATEGORIES.ACTIONS,
  //   type: 'Action_HttpRequest',
  //   label: 'HTTP Request',
  //   canvasIcon: '🌐',
  //   Icon: Globe,
  // },
  // {
  //   kind: 'node',
  //   id: 'action-update-record',
  //   category: SIDEBAR_CATEGORIES.ACTIONS,
  //   type: 'Action_UpdateRecord',
  //   label: 'Update CRM',
  //   canvasIcon: '✏️',
  //   Icon: Database,
  // },
  // {
  //   kind: 'node',
  //   id: 'action-emit-event',
  //   category: SIDEBAR_CATEGORIES.ACTIONS,
  //   type: 'Action_EmitEvent',
  //   label: 'Emit Event',
  //   canvasIcon: '⚡',
  //   Icon: Zap,
  // },
  // {
  //   kind: 'node',
  //   id: 'action-create-record',
  //   category: SIDEBAR_CATEGORIES.ACTIONS,
  //   type: 'Action_CreateRecord',
  //   label: 'Create Record',
  //   canvasIcon: '➕',
  //   Icon: Plus,
  // },
  // {
  //   kind: 'node',
  //   id: 'action-delete-record',
  //   category: SIDEBAR_CATEGORIES.ACTIONS,
  //   type: 'Action_DeleteRecord',
  //   label: 'Delete Record',
  //   canvasIcon: '🗑',
  //   Icon: Trash2,
  // },
  // {
  //   kind: 'node',
  //   id: 'action-logger',
  //   category: SIDEBAR_CATEGORIES.ACTIONS,
  //   type: 'Action_Logger',
  //   label: 'Logger',
  //   canvasIcon: '📝',
  //   Icon: FileText,
  // },
  // {
  //   kind: 'node',
  //   id: 'action-condition-check',
  //   category: SIDEBAR_CATEGORIES.ACTIONS,
  //   type: 'Action_ConditionCheck',
  //   label: 'Condition',
  //   canvasIcon: '⑂',
  //   Icon: GitBranch,
  // },
  // {
  //   kind: 'node',
  //   id: 'action-wait-delay',
  //   category: SIDEBAR_CATEGORIES.ACTIONS,
  //   type: 'Action_WaitDelay',
  //   label: 'Wait / Delay',
  //   canvasIcon: '⏳',
  //   Icon: Hourglass,
  // },

  // // FLOW CONTROL
  // {
  //   kind: 'node',
  //   id: 'flow-if-else',
  //   category: SIDEBAR_CATEGORIES.FLOW_CONTROL,
  //   type: 'Flow_IfElse',
  //   label: 'If / Else',
  //   canvasIcon: '↔️',
  //   Icon: Split,
  // },
  // {
  //   kind: 'node',
  //   id: 'flow-switch',
  //   category: SIDEBAR_CATEGORIES.FLOW_CONTROL,
  //   type: 'Flow_Switch',
  //   label: 'Switch',
  //   canvasIcon: '🔀',
  //   Icon: ListTree,
  // },
  // {
  //   kind: 'node',
  //   id: 'flow-loop',
  //   category: SIDEBAR_CATEGORIES.FLOW_CONTROL,
  //   type: 'Flow_Loop',
  //   label: 'Loop',
  //   canvasIcon: '🔁',
  //   Icon: RefreshCcw,
  // },
  // {
  //   kind: 'node',
  //   id: 'flow-parallel',
  //   category: SIDEBAR_CATEGORIES.FLOW_CONTROL,
  //   type: 'Flow_Parallel',
  //   label: 'Parallel',
  //   canvasIcon: '▦',
  //   Icon: LayoutGrid,
  // },
  // {
  //   kind: 'node',
  //   id: 'flow-merge',
  //   category: SIDEBAR_CATEGORIES.FLOW_CONTROL,
  //   type: 'Flow_Merge',
  //   label: 'Merge',
  //   canvasIcon: '⧇',
  //   Icon: GitMerge,
  // },
  // {
  //   kind: 'node',
  //   id: 'flow-error-handler',
  //   category: SIDEBAR_CATEGORIES.FLOW_CONTROL,
  //   type: 'Flow_ErrorHandler',
  //   label: 'Error Handler',
  //   canvasIcon: '⚠️',
  //   Icon: ShieldAlert,
  // },

  // // UTILITIES
  // {
  //   kind: 'node',
  //   id: 'util-transform',
  //   category: SIDEBAR_CATEGORIES.UTILITIES,
  //   type: 'Util_TransformData',
  //   label: 'Transform Data',
  //   canvasIcon: '🔄',
  //   Icon: Shuffle,
  // },
  // {
  //   kind: 'node',
  //   id: 'util-set-variable',
  //   category: SIDEBAR_CATEGORIES.UTILITIES,
  //   type: 'Util_SetVariable',
  //   label: 'Set Variable',
  //   canvasIcon: '{}',
  //   Icon: Braces,
  // },
  // {
  //   kind: 'node',
  //   id: 'util-logger',
  //   category: SIDEBAR_CATEGORIES.UTILITIES,
  //   type: 'Util_Logger',
  //   label: 'Logger',
  //   canvasIcon: '📝',
  //   Icon: Terminal,
  // },
  // {
  //   kind: 'node',
  //   id: 'util-comment',
  //   category: SIDEBAR_CATEGORIES.UTILITIES,
  //   type: 'Util_Comment',
  //   label: 'Comment',
  //   canvasIcon: '💭',
  //   Icon: MessageSquareMore,
  // },

  // // END (terminal)
  // {
  //   kind: 'node',
  //   id: 'terminal-end',
  //   category: SIDEBAR_CATEGORIES.TERMINAL,
  //   type: 'Flow_End',
  //   label: 'End',
  //   canvasIcon: '🏁',
  //   Icon: Flag,
  // },
];

/** All draggable node `type` strings grouped by category (excludes palette actions). */
export function buildCategoryNodeTypesFromItems(items: readonly SidebarPaletteItem[]): Record<SidebarCategoryId, string[]> {
  const map = {} as Record<SidebarCategoryId, string[]>;
  for (const key of [...SIDEBAR_COLLAPSIBLE_CATEGORY_ORDER, SIDEBAR_CATEGORIES.TERMINAL]) {
    map[key] = [];
  }
  for (const item of items) {
    if (item.kind !== 'node') continue;
    map[item.category].push(item.type);
  }
  const terminal = map[SIDEBAR_CATEGORIES.TERMINAL];
  for (const t of ['End', 'Flow_End']) {
    if (!terminal.includes(t)) terminal.push(t);
  }
  return map;
}

/** Derived from static `SIDEBAR_PALETTE_ITEMS` (fallback when API is unused). */
export const SIDEBAR_CATEGORY_NODE_TYPES: Record<SidebarCategoryId, string[]> = buildCategoryNodeTypesFromItems(
  SIDEBAR_PALETTE_ITEMS
);

export const SIDEBAR_DRAG_MIME = 'application/x-agentic-palette';

export type SidebarDragPayload = {
  type: string;
  label: string;
  icon: string;
  category: SidebarCategoryId;
  /** Category row `uuid` from WorkflowBuilderControls when present. */
  categoryUuid?: string;
  resourceCategory?: string;
  controlUuid?: string;
};

/** Fixed palette drag for the pinned End node (not from WorkflowBuilderControls). */
export const STATIC_FLOW_END_DRAG_PAYLOAD: SidebarDragPayload = {
  type: 'End',
  label: 'End',
  icon: '🏁',
  category: SIDEBAR_CATEGORIES.TERMINAL,
};

/**
 * Optional fields merged onto flow node `data` when placed from the builder palette.
 * Serialized inside React Flow `nodes[].data` in workflow definitions.
 */
export type WorkflowRegistryFields = {
  /** Sidebar/API category key (e.g. `actions`). */
  workflowCategoryid?: SidebarCategoryId;
  /** Control row uuid from WorkflowBuilderControls. */
  workflowControlid?: string;
  /** Category row uuid from WorkflowBuilderControls when present. */
  workflowCategoryuuid?: string;
};

/** Build registry fields for persistence (omits empty uuid strings). */
export function workflowBuilderRegistryFields(input: {
  category?: SidebarCategoryId;
  controlUuid?: string;
  categoryUuid?: string;
}): WorkflowRegistryFields {
  const out: WorkflowRegistryFields = {};
  if (input.category) out.workflowCategoryid = input.category;
  const cu = input.controlUuid?.trim();
  if (cu) out.workflowControlid = cu;
  const catu = input.categoryUuid?.trim();
  if (catu) out.workflowCategoryuuid = catu;
  return out;
}

export function workflowBuilderRegistryFromPayload(payload: SidebarDragPayload): WorkflowRegistryFields {
  return workflowBuilderRegistryFields({
    category: payload.category,
    controlUuid: payload.controlUuid,
    categoryUuid: payload.categoryUuid,
  });
}

/** True when payload targets a manual data-model row from WorkflowBuilderControls API. */
export function isManualMcpPalettePayload(payload: SidebarDragPayload): boolean {
  return (
    payload.category === SIDEBAR_CATEGORIES.DATA_MODELS_MCPS && payload.resourceCategory === 'Manual'
  );
}

/**
 * Normalize partial drag JSON (API may omit type/icon for Manual MCP rows).
 * Use after JSON.parse on `SIDEBAR_DRAG_MIME` data.
 */
export function normalizeSidebarDragPayload(parsed: Partial<SidebarDragPayload>): SidebarDragPayload | null {
  if (!parsed.category || !parsed.label) return null;
  if (!isSidebarCategoryIdString(parsed.category)) return null;

  const category = parsed.category;
  const rc =
    parsed.resourceCategory ??
    (parsed as { resource_category?: string }).resource_category;
  const manual = category === SIDEBAR_CATEGORIES.DATA_MODELS_MCPS && rc === 'Manual';

  if (!manual && (!parsed.type?.trim() || !parsed.icon)) return null;

  const controlKey =
    (parsed.controlUuid && String(parsed.controlUuid).replace(/[^a-zA-Z0-9_-]/g, '')) || '';
  const type =
    parsed.type?.trim() ||
    (manual ? `MCP_Manual_${controlKey || `t${Date.now()}`}` : '');
  if (!type) return null;

  const icon = parsed.icon?.trim() || (manual ? '🗄️' : '');
  if (!icon && !manual) return null;

  const categoryUuid =
    parsed.categoryUuid ?? (parsed as { category_uuid?: string }).category_uuid;

  return {
    type,
    label: parsed.label,
    icon,
    category,
    categoryUuid: categoryUuid?.trim() || undefined,
    resourceCategory: rc,
    controlUuid: parsed.controlUuid ?? (parsed as { uuid?: string }).uuid,
  };
}

export function sidebarPayloadFromDragJson(raw: string): SidebarDragPayload | null {
  try {
    return normalizeSidebarDragPayload(JSON.parse(raw) as Partial<SidebarDragPayload>);
  } catch {
    return null;
  }
}

function isSidebarCategoryIdString(value: string): value is SidebarCategoryId {
  return (Object.values(SIDEBAR_CATEGORIES) as string[]).includes(value);
}
